import { clientHasFeature, Features } from '../../../shared/features'

export type TranslationLocale = {
	code: string
	name?: string
	default?: boolean
}

export type TranslationField = {
	id: string
	required: boolean
	value?: Record<string, unknown>
}

type TranslationFieldSource = {
	typeName: string
	fieldName: string
	value?: unknown
}

type TranslationFieldDefinition = {
	id: string
	required?: boolean
	type?: string
	localized?: boolean
	items?: {
		linkContentType?: string[]
		validations?: Array<{ linkContentType?: string[] }>
	}
}

export type TranslationLocaleState = 'missing-required' | 'incomplete' | 'complete' | 'empty'

export function hasTranslationValue(value: unknown): boolean {
	if (typeof value === 'string') return value.length > 0 && value !== '<p></p>'
	if (Array.isArray(value)) return value.length > 0
	if (typeof value === 'boolean') return true
	return value !== null && value !== undefined
}

export function hasAnyTranslationValue(
	locales: TranslationLocale[],
	value?: Record<string, unknown>,
): boolean {
	if (!locales.length) return true
	return locales.some(locale => hasTranslationValue(value?.[locale.code]))
}

export function resolveTranslationFields(
	fields: TranslationFieldSource[],
	typeFieldsFor: (typeName: string) => TranslationFieldDefinition[] | undefined,
	isLocalized: (field: TranslationFieldDefinition) => boolean,
	localeCodes: string[],
	onWarning: (message: string, detail: unknown) => void = console.warn,
): TranslationField[] {
	const resolvedFields: TranslationField[] = []
	const addResolvedField = (
		typeName: string,
		fieldName: string,
		value: unknown,
		idPrefix = '',
		ancestors = new Set<object>(),
		depth = 0,
	) => {
		if (depth > 20) {
			onWarning('translation field nesting exceeds supported depth', { typeName, fieldName })
			return
		}
		const typeFields = typeFieldsFor(typeName)
		const fieldDef = typeFields?.find(field => field.id === fieldName)
		if (!fieldDef) {
			onWarning('field not found', { typeName, fieldName })
			return
		}

		if (isLocalized(fieldDef)) {
			resolvedFields.push({
				id: idPrefix + fieldDef.id,
				required: !!fieldDef.required,
				value: value as Record<string, unknown> | undefined,
			})
			return
		}

		const linkContentTypeValidation = fieldDef.items?.validations?.find(validation => validation.linkContentType)
		const nestedType = fieldDef.items?.linkContentType?.[0] ?? linkContentTypeValidation?.linkContentType?.[0]
		if (!nestedType) return
		const localizedValue = value as Record<string, unknown> | undefined
		const entries = Array.isArray(value)
			? value
			: localeCodes.map(localeCode => localizedValue?.[localeCode]).find(Array.isArray) ?? []
		const nestedFields = typeFieldsFor(nestedType) ?? []
		for (const [ index, entry ] of entries.entries()) {
			if (entry && typeof entry === 'object' && ancestors.has(entry)) {
				onWarning('cyclic translation entry ignored', { typeName: nestedType, index })
				continue
			}
			const nestedAncestors = new Set(ancestors)
			if (entry && typeof entry === 'object') nestedAncestors.add(entry)
			for (const nestedField of nestedFields) {
				addResolvedField(
					nestedType,
					nestedField.id,
					entry?.fields?.[nestedField.id],
					`${idPrefix}${fieldDef.id}.${index}.`,
					nestedAncestors,
					depth + 1,
				)
			}
		}
	}

	for (const field of fields ?? []) {
		addResolvedField(field.typeName, field.fieldName, field.value)
	}
	return resolvedFields
}

export function defaultRequiredLocaleCodes(
	locales: TranslationLocale[],
	defaultLocale?: TranslationLocale,
): string[] {
	const locale = defaultLocale ?? locales.find(locale => locale.default) ?? locales[0]
	return locale?.code ? [ locale.code ] : []
}

export const ENGLISH_CONTENT_LOCALE_CODE = 'en'

/**
 * The locale that must be filled for the client's content (MYS-7512).
 * A client with the english-content feature authors in English, every other client in the default locale (German).
 */
export function clientRequiredLocaleCodes(
	client: any,
	locales: TranslationLocale[],
	defaultLocale?: TranslationLocale,
): string[] {
	if (client && clientHasFeature(client, Features.ENGLISH_CONTENT)) return [ ENGLISH_CONTENT_LOCALE_CODE ]
	return defaultRequiredLocaleCodes(locales, defaultLocale)
}

/**
 * Builds the common status used by validation and all translation indicators.
 * A model field being required does not make it mandatory in every locale:
 * requiredLocaleCodes decides where it is mandatory. In another locale, an
 * untouched translation is allowed, but once started all required fields must
 * be completed.
 */
export function buildTranslationStatuses(
	locales: TranslationLocale[],
	fields: TranslationField[],
	requiredLocaleCodes: string[],
) {
	const requiredLocales = new Set(requiredLocaleCodes)

	return locales.map(locale => {
		const localeIsRequired = requiredLocales.has(locale.code)
		const started = fields.some(field => hasTranslationValue(field.value?.[locale.code]))
		const missingModelRequired = fields
			.filter(field => field.required && !hasTranslationValue(field.value?.[locale.code]))
			.map(field => field.id)

		const total = { required: 0, optional: 0 }
		const translated = { required: 0, optional: 0 }
		const missingFieldIds = { required: [] as string[], optional: [] as string[] }

		for (const field of fields) {
			const required = field.required && localeIsRequired
			const bucket = required ? 'required' : 'optional'
			const populated = hasTranslationValue(field.value?.[locale.code])
			total[bucket]++
			if (populated) translated[bucket]++
			else missingFieldIds[bucket].push(field.id)
		}

		let status: TranslationLocaleState
		if (localeIsRequired && missingModelRequired.length) status = 'missing-required'
		else if (!localeIsRequired && started && missingModelRequired.length) status = 'incomplete'
		else if (started || localeIsRequired) status = 'complete'
		else status = 'empty'

		const percentage = {
			required: total.required ? Math.round(100 * translated.required / total.required) : 100,
			optional: total.optional ? Math.round(100 * translated.optional / total.optional) : 100,
		}

		return { locale, total, translated, percentage, missingFieldIds, status, required: localeIsRequired, started }
	})
}

export function requiredLocalesAreComplete(statuses: ReturnType<typeof buildTranslationStatuses>): boolean {
	return statuses.every(status => status.status !== 'missing-required')
}

export function startedLocalesAreComplete(statuses: ReturnType<typeof buildTranslationStatuses>): boolean {
	return statuses.every(status => status.status !== 'incomplete')
}
